import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { debounce } from 'debounce';

import { API_DEBOUNCE_TIMEOUT } from 'constants/params';
import { StoryBoardTracks } from 'services/storyboard.service';
import { AspectRatioStory, TextPreset } from 'services/story.service';
import { AppThunk } from 'store/store';
import { isNull } from 'components/app/_helpers/_app.helper';
import { aggregator } from 'components/app/editor/_mediator/monitor/monitor-req.slice';
import { HandleStoryConfig } from 'components/app/editor/_mediator/editor.slice';
import { Backup, BackupHandlers, BackupTitle } from 'components/app/editor/_mediator/backup/backup.slice';

const SRC = 'monitor.slice.ts';

export type UpdatedTracks = Partial<{ -readonly [key in keyof typeof StoryBoardTracks]: true }>;
export type TitleCreditsData = {
  storyId: StoryId;
  preset: TextPreset;
  text: string;
  aspectRatio: AspectRatioStory;
  key: 'title' | 'credits';
};

interface SetMonitorPayload {
  backup?: Backup;
  thumbnail?: ClipId;
}

// SCHEME: addBackup()/uploadClipThumbnail() -> dispatchMonitor() -> debounce(aggregator()) -> [buffer -> pending] -> passMonitor()

export interface Monitor {
  isDirty: boolean;
  isMarkersUpdated: boolean;
  isRangeMarkersUpdated: boolean;
  isMarkerPresetUpdated: boolean;
  isStoryConfigUpdated: boolean;
  isCreditsUpdated: boolean;
  isTitleUpdated: boolean;
  titleCreditsData: TitleCreditsData | null;
  updatedTracks: UpdatedTracks | false;
  updatedClips: ClipId[];
  updatedFiles: FileId[];
  actions: BackupTitle[];
}

const initialState: Monitor = {
  isDirty: false,
  isMarkersUpdated: false,
  isRangeMarkersUpdated: false,
  isMarkerPresetUpdated: false,
  isStoryConfigUpdated: false,
  isCreditsUpdated: false,
  isTitleUpdated: false,
  updatedTracks: false,
  titleCreditsData: null,
  updatedClips: [],
  updatedFiles: [],
  actions: [],
};

// T H U N K S

export const dispatchMonitor =
  ({ backup, thumbnail }: SetMonitorPayload): AppThunk =>
  (dispatch, getState) => {
    const {
      setIsDirty,
      setIsMarkersUpdated,
      setIsMarkerPresetUpdated,
      setIsRangeMarkersUpdated,
      setIsStoryConfigUpdated,
      setIsTitleCreditsUpdated,
      setUpdatedTracks,
      addUpdatedClips,
      addUpdatedFiles,
      addAction,
    } = monitor.actions;
    const { selectedStory } = getState().projects;
    if (!selectedStory) {
      console.error(`[${SRC}] dispatchMonitor: story is not selected`);
      return;
    }
    if (thumbnail || backup) dispatch(setIsDirty());
    if (thumbnail) dispatch(addUpdatedClips([thumbnail]));
    if (backup) {
      /*
      /* files */
      if (backup.handler === BackupHandlers.handleFileConfigs) {
        const fileId = backup.data.fileId;
        if (fileId) dispatch(addUpdatedFiles(fileId));
      }

      /* markers */
      if (backup.handler === BackupHandlers.handleMarkers) {
        if (backup.data.markers) dispatch(setIsMarkersUpdated());

        /* markerPreset */
        const markerPresetId = backup.data.selectedMarkerPreset;
        if (markerPresetId) dispatch(setIsMarkerPresetUpdated());

        /* rangeMarkers */
        const rangeMarkers = backup.data.rangeMarkers;
        if (rangeMarkers && rangeMarkers.startMarkerId !== undefined && rangeMarkers.finishMarkerId !== undefined)
          dispatch(setIsRangeMarkersUpdated());
      }

      /* clips */
      if (backup.handler === BackupHandlers.handleClips) {
        const clipsMap = backup.data.clipsMap;
        if (clipsMap) dispatch(addUpdatedClips(Object.keys(clipsMap)));

        if (backup.data.markers) dispatch(setIsMarkersUpdated());

        /* storyBoard */
        const storyBoard = backup.data.storyBoard;
        const updatedTracks = Object.keys(storyBoard ?? {}) as StoryBoardTracks[];
        if (updatedTracks.length) {
          const updTracks: UpdatedTracks = {};
          updatedTracks.forEach((track) => (updTracks[track] = true));
          dispatch(setUpdatedTracks(updTracks));
        }
      }

      /* clipConfig */
      if (backup.handler === BackupHandlers.handleClipConfig) {
        const clipConfig = backup.data;
        if (clipConfig.config) dispatch(addUpdatedClips([clipConfig.clipId]));
      }

      /* storyConfig */
      if (backup.handler === BackupHandlers.handleStoryConfig) {
        const storyConfig = backup.data.storyConfig;
        if (storyConfig) dispatch(setIsStoryConfigUpdated());

        if (backup.data.markers) dispatch(setIsMarkersUpdated());

        /* storyConfig/titleCredits */
        const titleCreditsAction = backup.data.titleCreditsAction;
        if (titleCreditsAction) dispatch(setIsTitleCreditsUpdated(titleCreditsAction));
      }

      /* actions */
      if (backup.params) dispatch(addAction(backup.params.title));
    }
    debounce(() => dispatch(aggregator(selectedStory)), `aggregator`, API_DEBOUNCE_TIMEOUT);
  };
//

// S L I C E

export const monitor = createSlice({
  name: 'monitor',
  initialState,
  reducers: {
    setIsDirty: (state) => {
      state.isDirty = true;
    },
    setIsMarkersUpdated: (state) => {
      state.isMarkersUpdated = true;
    },
    setIsTitleCreditsUpdated: (state, action: PayloadAction<HandleStoryConfig['titleCreditsAction']>) => {
      switch (action.payload) {
        case 'credits':
          state.isCreditsUpdated = true;
          break;
        case 'title':
          state.isTitleUpdated = true;
          break;
        case 'both':
          state.isTitleUpdated = true;
          state.isCreditsUpdated = true;
      }
    },
    setTitleCreditsData: (state, action: PayloadAction<Monitor['titleCreditsData']>) => {
      state.titleCreditsData = action.payload;
    },
    setIsRangeMarkersUpdated: (state) => {
      state.isRangeMarkersUpdated = true;
    },
    setIsMarkerPresetUpdated: (state) => {
      state.isMarkerPresetUpdated = true;
    },
    setIsStoryConfigUpdated: (state) => {
      state.isStoryConfigUpdated = true;
    },
    setUpdatedTracks: (state, action: PayloadAction<UpdatedTracks>) => {
      if (isNull(state.updatedTracks)) state.updatedTracks = action.payload;
      else state.updatedTracks = { ...state.updatedTracks, ...action.payload };
    },
    addUpdatedClips: (state, action: PayloadAction<ClipId[]>) => {
      action.payload.forEach((clipId) => {
        if (!state.updatedClips.includes(clipId)) {
          state.updatedClips.push(clipId);
        }
      });
    },
    addUpdatedFiles: (state, action: PayloadAction<FileId>) => {
      if (!state.updatedFiles.includes(action.payload)) {
        state.updatedFiles.push(action.payload);
      }
    },
    addAction: (state, action: PayloadAction<BackupTitle>) => {
      state.actions.push(action.payload);
    },
    resetMonitor: () => {
      return initialState;
    },
  },
});

export const { resetMonitor, setTitleCreditsData } = monitor.actions;
export default monitor.reducer;
